import { defineAsyncComponent } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { streamTypes } from "shared/constants";
import applyThemeConfig from "shared/helpers/applyThemeConfig";
import { getCssVariableValue } from "shared/helpers/dom";
import transformKeys from "shared/helpers/transformKeys";

export default applyThemeConfig([
  {
    alias: ["/login", "/password-reset"],
    component: () => import("pages/LoginPage.vue"),
    meta: {
      // This var means a not logged user can access the route. Used by the navigation guard
      freeAccess: true,
      get statusBarColor() {
        return getCssVariableValue("--s-menu-default");
      },
      statusBarStyle: "Light",
    },
    name: "login",
    path: "/sign-in",
    props: (route) => ({
      loginCode: route.query.login_code,
      redirect: route.query.redirect,
      resetKey: route.query.reset_key,
    }),
  },
  {
    component: () => import("shared/pages/OauthLogin.vue"),
    meta: {
      freeAccess: true,
      public: true,
    },
    path: "/oauth",
    props: (route) => ({
      ...route.params,
      ...transformKeys(route.query, "camelCase"),
    }),
  },
  {
    component: () => import("shared/pages/PlanhatPage.vue"),
    name: "planhat",
    path: "/planhat",
  },
  {
    component: () => import("shared/pages/SkilljarPage.vue"),
    name: "skilljar",
    path: "/skilljar",
  },

  {
    children: [
      {
        alias: "/mention-detail/:mentionId/:streamId/:mentionType",
        component: () => import("pages/MentionDetailPage"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_mention_details"),
              name: "show-action-sheet-mention-detail",
            },
          ],
          pushed: true,
        },
        name: "mention-detail",
        path: "/mention-detail/:mentionId/:streamId/:mentionType",
        props: (route) => ({
          mentionId: route.params.mentionId,
          mentionType: route.params.mentionType,
          streamId: route.params.streamId,
        }),
      },
      {
        component: () => import("pages/MainPage.vue"),
        meta: {
          showAIAssistant: true,
          get statusBarColor() {
            return getCssVariableValue("--s-menu-default");
          },
          statusBarStyle: "Light",
        },
        name: "launchpad",
        path: "",
      },
      {
        meta: {
          hideIntercom: true,
        },
        name: "search",
        path: "search",
      },
      {
        component: () =>
          import("shared/components/streams/preview/PaperArticlePreview.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_paper_article"),
              name: "show-action-sheet-paper-article",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.print_item"),
        },
        name: "paper_article",
        path: "paper_articles/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () => import("src/components/MentionDetailArticle.vue"),
        meta: {
          articleType: "article",
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () => getLocaleText("routes.buttons.action_sheet_article"),
              name: "show-action-sheet-article",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.online_item"),
        },
        name: "article",
        path: "articles/:id/:streamId?",
        props: (route) => ({
          articleType: route.meta.articleType,
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () =>
          import("shared/components/streams/preview/MagazineArticlePreview.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_magazine_article"),
              name: "show-action-sheet-magazine-article",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.magazine_item"),
        },
        name: "magazine_article",
        path: "magazine_articles/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () =>
          import("shared/components/streams/preview/TvPreview.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () => getLocaleText("routes.buttons.action_sheet_caption"),
              name: "show-action-sheet-caption",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.media_item"),
          tvType: "tv_caption",
        },
        name: "tv_caption",
        path: "tv_captions/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          tvType: route.meta.tvType,
        }),
      },
      {
        component: () =>
          import("shared/components/streams/preview/TvPreview.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_tv_super"),
              name: "show-action-sheet-caption",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.media_item"),
          tvType: "tv_super",
        },
        name: "tv_super",
        path: "tv_supers/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
          tvType: route.meta.tvType,
        }),
      },
      {
        component: () =>
          import("shared/components/streams/preview/TvPreview.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_tv_logo_appearance"),
              name: "show-action-sheet-caption",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.media_item"),
          tvType: "tv_logo_appearance",
        },
        name: "tv_logo_appearance",
        path: "tv_logo_appearances/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
          tvType: route.meta.tvType,
        }),
      },
      {
        component: () => import("src/components/MentionDetailRadio.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_radio_clip"),
              name: "show-action-sheet-radio-clip",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.media_item"),
        },
        name: "radio_clip",
        path: "radio_clips/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () => import("src/components/MentionDetailPodcast.vue"),
        meta: {
          buttons: [
            {
              icon: "more_vert",
              iconType: "material",
              label: () =>
                getLocaleText("routes.buttons.action_sheet_podcast_episode"),
              name: "show-action-sheet-podcast-episode",
            },
          ],
          hideIntercom: true,
          pushed: true,
          title: () => getLocaleText("routes.media_item"),
        },
        name: "podcast_episode",
        path: "podcast_episodes/:id/:streamId?",
        props: (route) => ({
          id: route.params.id,
          keywords: [].concat(route.query.keywords || []),
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "tweet",
        path: "tweets/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "tweet",
        }),
      },
      {
        component: () =>
          import("shared/components/streams/preview/TweetPreview.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "tweet_preview",
        path: "tweets/:id",
        props: (route) => ({
          id: route.params.id,
          product: route.query.product,
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "facebook_post",
        path: "facebook_posts/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "facebook_post",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "instagram_post",
        path: "instagram_posts/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "instagram_post",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "reddit_post",
        path: "reddit_posts/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "reddit_post",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "blog_post",
        path: "blog_posts/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "blog_post",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "forum_post",
        path: "forum_posts/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "forum_post",
        }),
      },
      {
        component: () => import("src/components/MentionDetailSocial.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "youtube_video",
        path: "youtube_videos/",
        props: (route) => ({
          keywords: [].concat(route.query.keywords || []),
          socialMention: route.params.tweetMention,
          socialType: "youtube_video",
        }),
      },
      {
        component: () => import("pages/FrontpagesPage.vue"),
        meta: {
          buttons: [
            {
              color: "#b2b2b2",
              icon: "ion-md-settings",
              label: () =>
                getLocaleText("routes.buttons.set_tracked_publishers"),
              name: "set-tracked-publishers",
            },
          ],
          showAIAssistant: true,
          topComponent: defineAsyncComponent({
            loader: () => import("src/components/MenuDropdown.vue"),
          }),
        },
        name: "front-pages",
        path: "front-pages",
      },
      {
        component: () => import("pages/DailyHighlightsPage.vue"),
        meta: {
          leftIcon: "ion-ios-arrow-back",
          showAIAssistant: true,
          title: () => getLocaleText("routes.daily_highlights"),
        },
        name: "daily-highlights",
        path: "daily-highlights",
      },
      {
        component: () => import("pages/DailyHighlightDetailPage.vue"),
        meta: {
          pushed: true,
          showAIAssistant: true,
        },
        path: "daily-highlights/:id",
        props: true,
      },
      {
        component: () => import("pages/MediaTranscriptRequests.vue"),
        meta: {
          fitScreen: true,
          showAIAssistant: true,
          title: () => getLocaleText("routes.media_and_transcripts"),
        },
        name: "media-and-transcripts",
        path: "media-and-transcripts",
      },
      {
        component: () => import("pages/StreamsPage.vue"),
        meta: {
          buttons: [
            {
              color: "#b2b2b2",
              feature: "add_stream",
              icon: "icon icon-plus-squared",
              label: () => getLocaleText("routes.buttons.add_mention_stream"),
              name: "add-mention-stream",
            },
          ],
          showAIAssistant: true,
          title: () => getLocaleText("routes.mention_streams"),
        },
        name: "streams",
        path: "streams",
      },
      {
        component: () => import("pages/StreamPage.vue"),
        meta: {
          buttons: [
            {
              class: "bold",
              color: "#b2b2b2",
              icon: "filter_alt",
              iconType: "material",
              label: () => getLocaleText("routes.buttons.filter"),
              name: "filter",
              size: 25,
              toggleable: true,
            },
            {
              color: "#b2b2b2",
              icon: "ion-md-settings",
              label: () => getLocaleText("routes.buttons.edit_mention_stream"),
              name: "edit-mention-stream",
              size: 25,
            },
          ],
          showAIAssistant: true,
        },
        name: "stream",
        path: "streams/:groupSlug/:streamSlug",
        props: (route) => ({
          groupSlug: route.params.groupSlug,
          streamSlug: route.params.streamSlug,
        }),
      },
      {
        children: [
          {
            component: () => import("shared/pages/StreamNotificationsEdit.vue"),
            meta: {
              showNavButtons: true,
              title: () => getLocaleText("routes.notifications"),
            },
            name: "socialStreamNotificationsEdit",
            path: "stream-notifications/:notificationId?",
            props: true,
          },
          {
            component: () => import("pages/SocialStreamPage.vue"),
            meta: {
              buttons: [
                {
                  class: "bold",
                  color: "#b2b2b2",
                  icon: "filter_alt",
                  iconType: "material",
                  label: () => getLocaleText("routes.buttons.filter"),
                  name: "filter",
                  size: 25,
                  toggleable: true,
                },
                {
                  color: "#b2b2b2",
                  icon: "ion-md-settings",
                  label: () =>
                    getLocaleText("routes.buttons.edit_social_stream"),
                  name: "edit-mention-stream",
                  size: 25,
                },
              ],
              showAIAssistant: true,
            },
            name: "social-monitor",
            path: "monitor/:groupSlug/:streamSlug",
            props: (route) => ({
              groupSlug: route.params.groupSlug,
              streamSlug: route.params.streamSlug,
            }),
          },
          {
            component: () => import("src/pages/MentionsStreamBuilderPage.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              hideIntercom: true,
              showNavButtons: true,
              title: () => getLocaleText("routes.add_social_stream"),
            },
            name: "social-stream-new",
            path: "social-stream/new",
            props: (route) => ({
              streamType: streamTypes.socialStream,
              ...route.params,
            }),
          },
          {
            component: () =>
              import("src/pages/Social/SocialStreamNotificationEdit.vue"),
            meta: {
              buttons: [
                {
                  icon: "ion-md-close",
                  label: () =>
                    getLocaleText(
                      "routes.buttons.close_mention_stream_notification"
                    ),
                  name: "close-mention-stream-notification",
                  size: 20,
                },
              ],
              fitScreen: true,
              title: () => getLocaleText("routes.social_stream_notification"),
            },
            name: "social-stream-notification-edit",
            path: "monitor/:groupSlug/:streamSlug/edit/notification/:notificationId?",
            props: true,
          },
          {
            component: () => import("src/pages/Social/SocialStreamEdit.vue"),
            meta: {
              buttons: [
                {
                  icon: "ion-md-close",
                  label: () =>
                    getLocaleText("routes.buttons.close_social_stream"),
                  name: "close-mention-stream",
                  size: 20,
                },
              ],
              fitScreen: true,
              hideIntercom: true,
              reuseComponent: true,
              showNavButtons: true,
              title: () => getLocaleText("routes.edit_social_stream"),
            },
            name: "social-stream-edit",
            path: "monitor/:groupSlug/:streamSlug/edit/:tab?",
            props: true,
          },
          {
            component: () =>
              import("src/pages/Social/SocialStreamGroupEdit.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              hideIntercom: true,
              showNavButtons: true,
              title: () => getLocaleText("routes.stream_group_edit"),
            },
            name: "social-stream-group-edit",
            path: "social-stream/group/:groupSlug/edit",
            props: true,
          },
          {
            name: "social-monitor-shared",
            path: "social-stream/shared",
            redirect: { name: "social-monitor" },
          },
          {
            component: () => import("pages/BookmarkStream.vue"),
            meta: {
              buttons: [
                {
                  color: "#b2b2b2",
                  icon: "filter_alt",
                  iconType: "material",
                  label: () => getLocaleText("routes.buttons.filter"),
                  name: "filter",
                  size: 25,
                  toggleable: true,
                },
                {
                  color: "#b2b2b2",
                  icon: "ion-md-settings",
                  label: () =>
                    getLocaleText("routes.buttons.edit_bookmark_stream"),
                  name: "edit-bookmark-stream",
                  size: 25,
                },
              ],
              showAIAssistant: true,
            },
            name: "socialBookmarkStream",
            path: "social-bookmark-stream/:groupSlug/:streamSlug",
            props: (route) => ({
              ...route.params,
              isSocialStream: true,
            }),
          },
          {
            component: () => import("pages/BookmarkStreamEdit.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              hideIntercom: true,
              showNavButtons: true,
              title: () => getLocaleText("routes.tagged_folder_edit"),
            },
            name: "social-bookmark-stream-edit",
            path: "social-bookmark-stream/:groupSlug/:streamSlug/edit",
            props: (route) => ({
              ...route.params,
              isSocialStream: true,
            }),
          },
        ],
        component: () => import("pages/Social/SocialPage.vue"),
        meta: {
          product: "social",
        },
        name: "social",
        path: "social",
      },
      {
        name: "shared-stream",
        path: "aezaeaz",
        redirect: { name: "stream" },
      },
      {
        component: () => import("pages/BookmarkStreamEdit.vue"),
        meta: {
          fitScreen: true,
          hasClose: true,
          hideIntercom: true,
          showNavButtons: true,
          title: () => getLocaleText("routes.tagged_folder_edit"),
        },
        name: "bookmark-stream-edit",
        path: "bookmark-stream/:groupSlug/:streamSlug/edit",
        props: true,
      },
      {
        component: () => import("pages/BookmarkStream.vue"),
        meta: {
          buttons: [
            {
              color: "#b2b2b2",
              icon: "filter_alt",
              iconType: "material",
              label: () => getLocaleText("routes.buttons.filter"),
              name: "filter",
              size: 25,
              toggleable: true,
            },
            {
              color: "#b2b2b2",
              icon: "ion-md-settings",
              label: () => getLocaleText("routes.buttons.edit_bookmark_stream"),
              name: "edit-bookmark-stream",
              size: 25,
            },
          ],
        },
        name: "bookmarkStream",
        path: "bookmark-stream/:groupSlug/:streamSlug",
        props: true,
      },
      {
        component: () => import("pages/AlertsPage.vue"),
        meta: {
          pushed: true,
          title: () => getLocaleText("routes.alerts"),
        },
        name: "alerts",
        path: "alerts",
      },
      {
        component: () => import("pages/MentionPreviewAlert.vue"),
        meta: {
          hideIntercom: true,
          pushed: true,
        },
        name: "mentionAlert",
        path: "mention-alert/:category/:mentionId/:streamId?",
        props: (route) => ({
          category: route.params.category,
          keywords: [].concat(route.query.keywords || []),
          mentionId: route.params.mentionId,
          streamId: route.params.streamId || "",
        }),
      },
      {
        component: () => import("pages/JournalistView.vue"),
        meta: {
          fitScreen: true,
          hasClose: true,
          title: "",
        },
        name: "journalist-view",
        path: "journalist",
        props: (route) => ({
          author: route.query.author,
          mention: JSON.parse(window.history.state.mention),
        }),
      },
      {
        component: () => import("shared/components/user/ContactMenu.vue"),
        meta: {
          title: () => getLocaleText("routes.help"),
        },
        name: "help",
        path: "help",
      },
      {
        children: [
          {
            component: () => import("src/pages/MentionsStreamBuilderPage.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              title: () => getLocaleText("routes.mention_stream_create"),
            },
            name: "mention-stream-new",
            path: "new",
            props: true,
          },
          {
            component: () =>
              import("src/pages/MentionStream/MentionStreamGroupEdit.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              title: () => getLocaleText("routes.stream_group_edit"),
            },
            name: "mention-stream-group-edit",
            path: "group/:groupSlug/edit",
            props: true,
          },
          {
            component: () => import("src/pages/MentionsStreamBuilderPage.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              title: () => getLocaleText("routes.mention_stream_create"),
            },
            name: "mention-stream-new",
            path: "new/:groupSlug?",
            props: true,
          },
          {
            component: () =>
              import("src/pages/MentionStream/MentionStreamNotificationEdit.vue"),
            meta: {
              buttons: [
                {
                  icon: "ion-md-close",
                  label: () =>
                    getLocaleText(
                      "routes.buttons.close_mention_stream_notification"
                    ),
                  name: "close-mention-stream-notification",
                  size: 20,
                },
              ],
              fitScreen: true,
              title: () => getLocaleText("routes.mention_stream_notification"),
            },
            name: "mention-stream-notification-edit",
            path: ":groupSlug/:streamSlug/edit/notification/:notificationId?",
            props: true,
          },
          {
            component: () =>
              import("src/pages/MentionStream/MentionStreamEdit.vue"),
            meta: {
              buttons: [
                {
                  icon: "ion-md-close",
                  label: () =>
                    getLocaleText("routes.buttons.close_mention_stream"),
                  name: "close-mention-stream",
                  size: 20,
                },
              ],
              fitScreen: true,
              reuseComponent: true,
              title: () => getLocaleText("routes.mention_stream_edit"),
            },
            name: "mention-stream-edit",
            path: ":groupSlug/:streamSlug/edit/:tab?",
            props: true,
          },
          {
            component: () => import("src/pages/MentionsStreamBuilderPage.vue"),
            meta: {
              fitScreen: true,
              hasClose: true,
              title: () => getLocaleText("routes.mention_stream_create"),
            },
            name: "mention-stream",
            path: "",
            props: true,
          },
        ],
        component: () => import("src/pages/MentionStream/MentionStream.vue"),
        path: "streams",
      },
    ],
    component: () => import("layouts/DefaultLayout.vue"),
    path: "/",
  },
]);
